import React, { useEffect, useMemo, useRef, useState } from "react";
import QRCode from "react-qr-code";
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import {
  clipExcerptWords,
  downloadQrFromSvg,
  excerptWordCount,
  EXCERPT_EXTRA_PX,
  EXCERPT_WORD_MAX,
  luxuryCardDetails,
  MINIMAL_QR_PX,
  LuxuryCardFieldSource,
} from "../../helpers/collectorQrDownload";
import { publicPieceUrl } from "../../helpers/collectorIds";
import { getItemQr } from "../../services/collectorApi";
import notifications from "../../common/Components/Notifications/Notification";
import "./my-collection.scss";
import { HKPImage } from "../../media/HKPImage";

type Props = {
  itemId?: string;
  collectorItemId?: string;
  title: string;
  story?: string;
  coverPhotoUrl?: string;
  fields?: LuxuryCardFieldSource;
  isOpen: boolean;
  onClose: () => void;
  initialFormat?: "minimal" | "luxury";
  onFormatChange?: (format: "minimal" | "luxury") => void;
  /** When true, only the print card is shown (download-card preview). */
  cardOnly?: boolean;
};

const QrPrintModal: React.FC<Props> = ({
  itemId,
  collectorItemId,
  title,
  story,
  coverPhotoUrl,
  fields,
  isOpen,
  onClose,
  initialFormat = "luxury",
  onFormatChange,
  cardOnly = false,
}) => {
  const [format, setFormat] = useState<"minimal" | "luxury">(initialFormat);
  const [qrUrl, setQrUrl] = useState("");
  const [storyExcerpt, setStoryExcerpt] = useState(() => clipExcerptWords(story || ""));
  const [error, setError] = useState("");
  const [downloading, setDownloading] = useState(false);
  const printRef = useRef<HTMLDivElement>(null);
  const details = useMemo(() => luxuryCardDetails(fields), [fields]);

  useEffect(() => {
    if (!isOpen) return;
    setFormat(initialFormat);
    setError("");
    setStoryExcerpt(clipExcerptWords(story || ""));
    const immediate = publicPieceUrl(collectorItemId || "");
    if (immediate) {
      setQrUrl(immediate);
    }
    if (!itemId) {
      if (!immediate) {
        setError("This piece does not have a public QR yet.");
      }
      return;
    }
    getItemQr(itemId)
      .then((res) => {
        const data = (res.data?.data || {}) as Record<string, unknown>;
        const id = String(data.collectorItemId || collectorItemId || "");
        const next = publicPieceUrl(id, String(data.qrPayloadUrl || data.publicPath || ""));
        if (next) setQrUrl(next);
        if (data.story && !(story && story.trim())) {
          setStoryExcerpt(clipExcerptWords(String(data.story)));
        }
      })
      .catch((err) => {
        const code = err?.response?.data?.message || err?.response?.status;
        if (code === "QR_NOT_AVAILABLE" || err?.response?.status === 403) {
          setError("Public cards are only for pieces you keep in My pieces.");
          return;
        }
        if (!immediate) {
          setError("Could not load the QR code. Try again.");
        }
      });
  }, [isOpen, itemId, collectorItemId, initialFormat, cardOnly, story]);

  const downloadCard = async () => {
    const svg = printRef.current?.querySelector("svg") || null;
    if (!svg || !qrUrl) {
      setError("QR is not ready to download.");
      return;
    }
    setDownloading(true);
    setError("");
    try {
      await downloadQrFromSvg(svg, {
        title,
        format,
        excerpt: storyExcerpt.trim(),
        coverUrl: coverPhotoUrl,
        itemId,
        details: format === "luxury" ? details : undefined,
      });
      notifications({
        title: "Downloaded",
        message: format === "luxury" ? "Luxury 4×6 card saved." : "QR card saved.",
        type: "success",
      });
    } catch {
      setError("Could not download the card. Try Print instead.");
    } finally {
      setDownloading(false);
    }
  };

  const qrSize = format === "minimal" ? MINIMAL_QR_PX : 96;
  const showingLuxury = format === "luxury";

  return (
    <>
      <style>{`
        @media print {
          body * { visibility: hidden; }
          #mc-qr-print-area, #mc-qr-print-area * { visibility: visible; }
          #mc-qr-print-area { position: absolute; left: 0; top: 0; }
          .mc-qr-luxury-card {
            width: 4in !important;
            height: 6in !important;
            max-width: none !important;
            transform: none !important;
          }
        }
        .mc-qr-minimal-card {
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          background: #fff;
          padding: 16px 12px;
          width: ${MINIMAL_QR_PX + EXCERPT_EXTRA_PX + 32}px;
          box-sizing: border-box;
        }
        .mc-qr-minimal-card .mc-qr-title,
        .mc-qr-minimal-card .mc-qr-brand {
          width: ${MINIMAL_QR_PX + EXCERPT_EXTRA_PX}px;
          margin-left: auto;
          margin-right: auto;
          text-align: center;
        }
      `}</style>
      <Modal isOpen={isOpen} toggle={onClose} size="lg" className="mc-qr-modal">
        <ModalHeader toggle={onClose}>
          {cardOnly ? "Preview print card" : "Download printable QR"}
        </ModalHeader>
        <ModalBody>
          {cardOnly && (
            <div className="mb-3">
              <button
                type="button"
                className={`mc-toggle me-2 ${format === "minimal" ? "is-active" : ""}`}
                onClick={() => {
                  setFormat("minimal");
                  onFormatChange?.("minimal");
                }}
              >
                Minimal
              </button>
              <button
                type="button"
                className={`mc-toggle ${format === "luxury" ? "is-active" : ""}`}
                onClick={() => {
                  setFormat("luxury");
                  onFormatChange?.("luxury");
                }}
              >
                Luxury 4×6 (photo)
              </button>
            </div>
          )}
          {showingLuxury && (
            <div className="mb-3">
              <p className="small text-muted mb-2">
                Printed size is 4×6 inches. Photo is about 2.7 inches tall with half-inch side margins.
                Up to four public details, then a 1×1 inch QR. Price and purchase location never appear.
              </p>
              <label className="form-label small" htmlFor="mc-qr-story-excerpt">Story on the card (up to 30 words)</label>
              <textarea
                id="mc-qr-story-excerpt"
                className="form-control form-control-sm"
                rows={3}
                value={storyExcerpt}
                onChange={(e) => setStoryExcerpt(clipExcerptWords(e.target.value))}
              />
              <small className="text-muted">{excerptWordCount(storyExcerpt)} / {EXCERPT_WORD_MAX} words</small>
            </div>
          )}
          {error && <p className="text-danger small" role="alert">{error}</p>}
          <div id="mc-qr-print-area" ref={printRef} className="d-flex justify-content-center">
            {showingLuxury ? (
              <div className="mc-qr-luxury-scale">
              <div className="mc-qr-luxury-card">
                {coverPhotoUrl ? (
                  <HKPImage allowMaster={false} preset="CATALOGUE" legacyUrl={coverPhotoUrl} alt="" className="cover" loading="eager" />
                ) : (
                  <div className="mc-qr-luxury-photo-fallback" aria-hidden="true" />
                )}
                {title && <div className="mc-qr-title">{title}</div>}
                <ul className="mc-qr-details">
                  {details.map((detail) => (
                    <li key={detail.label}>
                      <span>{detail.label}</span>
                      {detail.value}
                    </li>
                  ))}
                </ul>
                <p className="mc-qr-excerpt">{storyExcerpt.trim()}</p>
                {qrUrl ? (
                  <div className="mc-qr-svg-wrap">
                    <QRCode
                      value={qrUrl}
                      size={qrSize}
                      viewBox="0 0 256 256"
                      bgColor="#FFFFFF"
                      fgColor="#000000"
                    />
                  </div>
                ) : (
                  <p className="small text-muted mb-0">Preparing QR…</p>
                )}
                <div className="mc-qr-brand">HastKalaPramanak</div>
              </div>
              </div>
            ) : (
              <div className="mc-qr-minimal-card">
                {qrUrl ? (
                  <div className="mc-qr-svg-wrap-minimal">
                    <QRCode
                      value={qrUrl}
                      size={qrSize}
                      style={{ height: qrSize, width: qrSize }}
                      viewBox="0 0 256 256"
                      bgColor="#FFFFFF"
                      fgColor="#000000"
                    />
                  </div>
                ) : (
                  <p className="text-muted small mb-0">Preparing QR…</p>
                )}
                {title && <div className="mc-qr-title mt-2">{title}</div>}
                <div className="mc-qr-brand small mt-1">HastKalaPramanak · My Collection</div>
              </div>
            )}
          </div>
          {qrUrl && (
            <p className="small text-muted text-center mt-3 mb-0">
              Scan opens{" "}
              <a href={qrUrl} target="_blank" rel="noreferrer">{qrUrl}</a>
            </p>
          )}
        </ModalBody>
        <ModalFooter>
          <Button className="mc-btn-ghost" onClick={onClose}>Close</Button>
          {!cardOnly && (
            <Button className="mc-btn-secondary" onClick={() => window.print()} disabled={!qrUrl}>
              Print / Save as PDF
            </Button>
          )}
          <Button className="mc-btn-primary" onClick={downloadCard} disabled={!qrUrl || downloading}>
            {downloading
              ? "Downloading…"
              : showingLuxury
                ? "Download card"
                : "Download QR"}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default QrPrintModal;
